import type {
  ComponentVisibility,
  FormComponent,
  FormModule,
  FormStep,
  ValidationRules,
} from '@commenda/types';

/**
 * Treats `''`, `null`, `undefined`, empty arrays, and "nothing selected"
 * checkbox-group maps as empty. `0` and a standalone `false` are NOT empty —
 * required-field validation must accept them.
 *
 * CheckboxGroup / multi-select store selections as a `{ optionValue: boolean }`
 * map and set an unchecked option to `false` rather than deleting the key, so an
 * empty or all-`false` map means nothing is selected.
 */
export function isValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isBooleanSelectionMap(value)) return !Object.values(value).some(Boolean);
  return false;
}

// A plain object whose values are all booleans — the shape CheckboxGroup writes.
// Excludes File/Date/class instances (non-Object prototype) so value-bearing
// objects like addresses or file metadata are never treated as empty.
function isBooleanSelectionMap(
  value: unknown
): value is Record<string, boolean> {
  if (typeof value !== 'object' || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return false;
  return Object.values(value).every((v) => typeof v === 'boolean');
}

// 'read' is the canonical read-only marker. 'read-only' appears in
// agent-authored DSLs but is NOT honored by the wrappers (they check for
// 'read' only, so the field renders editable) — yet OS pre-fill ignores it
// too, so no value ever arrives on its own. Preview honors the author's
// intent (read-only ⇒ required-exempt) so runs aren't falsely halted, and the
// preview lint flags the invalid mode value itself.
const READ_ONLY_MODES = new Set(['read', 'read-only']);

export function isComponentReadOnly(component: FormComponent): boolean {
  const props = component.props ?? {};
  const dataSourceMode = component.dataSource?.commendaOS?.mode;
  return (
    (typeof dataSourceMode === 'string' && READ_ONLY_MODES.has(dataSourceMode)) ||
    (typeof props.mode === 'string' && READ_ONLY_MODES.has(props.mode)) ||
    props.disabled === true
  );
}

export function getDefaultExpandedModulesForStep(
  stepData?: FormStep,
  isModuleVisible?: (module: FormModule) => boolean
): Set<string> {
  const expanded = new Set<string>();
  const modules = stepData?.modules ?? [];
  if (modules.length === 0) return expanded;

  const visibleModules = modules.filter((module) =>
    isModuleVisible ? isModuleVisible(module) : true
  );
  const firstVisibleModuleId = visibleModules[0]?.id;
  const singleVisibleModuleId =
    visibleModules.length === 1 ? visibleModules[0]?.id : undefined;

  modules.forEach((module) => {
    if (isModuleVisible && !isModuleVisible(module)) return;
    if (
      module.id === firstVisibleModuleId ||
      module.id === singleVisibleModuleId
    ) {
      expanded.add(module.id);
    }
  });

  return expanded;
}

// Rewrites sibling field references inside a visibility condition so they
// resolve against a repeatable instance (e.g. `email` -> `directors.0.email`).
// Leaves already-dotted paths alone so cross-module references still work.
function rewriteConditionForInstance(
  cond: ComponentVisibility['condition'] | undefined,
  prefix: string
): ComponentVisibility['condition'] | undefined {
  if (!cond) return cond;
  const next = { ...cond };
  if (cond.field && !cond.field.includes('.')) {
    next.field = `${prefix}${cond.field}`;
  }
  if (cond.and) {
    next.and = cond.and
      .map((c) => rewriteConditionForInstance(c, prefix))
      .filter((c): c is NonNullable<typeof c> => c != null);
  }
  if (cond.or) {
    next.or = cond.or
      .map((c) => rewriteConditionForInstance(c, prefix))
      .filter((c): c is NonNullable<typeof c> => c != null);
  }
  return next;
}

export function isInstanceFieldVisible(
  visibility: ComponentVisibility | undefined,
  instanceData: Record<string, unknown>,
  parentIsFieldVisible: (visibility?: ComponentVisibility) => boolean,
  componentMap: Record<string, FormComponent>
): boolean {
  if (!visibility) return true;

  const { condition } = visibility;
  if (
    condition.field &&
    condition.operator &&
    (condition.value !== undefined || condition.operator === 'isNotEmpty')
  ) {
    const fieldValue = (instanceData ?? {})[condition.field];
    if (fieldValue !== undefined) {
      const fieldComponent = componentMap?.[condition.field];

      if (
        fieldComponent?.component === 'CheckboxGroup' &&
        typeof fieldValue === 'object' &&
        fieldValue !== null &&
        !Array.isArray(fieldValue)
      ) {
        const checkboxValue = fieldValue as Record<string, boolean>;
        const optionKey = String(condition.value);
        switch (condition.operator) {
          case 'equals':
            return checkboxValue[optionKey] === true;
          case 'notEquals':
            return checkboxValue[optionKey] !== true;
          case 'contains':
            return Object.entries(checkboxValue).some(
              ([key, checked]) => checked === true && key === optionKey
            );
          case 'isNotEmpty':
            return Object.values(checkboxValue).some(
              (checked) => checked === true
            );
          default:
            return true;
        }
      }

      switch (condition.operator) {
        case 'equals':
          return fieldValue === condition.value;
        case 'notEquals':
          return fieldValue !== condition.value;
        case 'greaterThan':
          return Number(fieldValue) > Number(condition.value);
        case 'lessThan':
          return Number(fieldValue) < Number(condition.value);
        case 'contains':
          if (Array.isArray(fieldValue)) {
            return fieldValue.some((item) => item === condition.value);
          }
          return String(fieldValue ?? '').includes(String(condition.value));
        case 'isNotEmpty':
          return Array.isArray(fieldValue)
            ? fieldValue.length > 0
            : fieldValue !== null &&
                fieldValue !== undefined &&
                fieldValue !== '';
        default:
          return true;
      }
    }
  }

  return parentIsFieldVisible(visibility);
}

// Rewrites sibling field references inside validation rules: notSameAs.field
// and each rule's `when` condition. Used by step/submit validators so rules
// referencing a sibling inside the same repeatable instance resolve correctly.
export function rewriteRulesForInstance(
  rules: ValidationRules | undefined,
  prefix: string
): ValidationRules | undefined {
  if (!rules) return rules;
  const next: ValidationRules = { ...rules };

  const ruleKeys: (keyof ValidationRules)[] = [
    'required',
    'minLength',
    'maxLength',
    'min',
    'max',
    'pattern',
    'email',
    'phone',
    'url',
    'custom',
    'fileSize',
    'address',
    'api',
    'notSameAs',
    'uniqueInRepeatable',
  ];

  for (const key of ruleKeys) {
    const r = next[key];
    if (typeof r !== 'object' || r === null) continue;
    if (!('when' in r) || !r.when) continue;
    const rewrittenWhen = rewriteConditionForInstance(
      r.when as ComponentVisibility['condition'],
      prefix
    );
    if (rewrittenWhen !== r.when) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (next as any)[key] = { ...(r as object), when: rewrittenWhen };
    }
  }

  if (next.notSameAs?.field && !next.notSameAs.field.includes('.')) {
    next.notSameAs = {
      ...next.notSameAs,
      field: `${prefix}${next.notSameAs.field}`,
    };
  }

  return next;
}

/**
 * The module a field lives in. A collapsed module is unmounted, not hidden, so a
 * failed validation there renders no error and the Next/Submit button just looks
 * dead — callers use this to open the section before reporting the error.
 *
 * A repeatable module stores instances under `saveKey`, so its fields arrive as
 * `saveKey.<index>.<fieldId>` and are matched on that prefix.
 */
export function findModuleIdForField(
  step: FormStep | undefined,
  fieldId: string | undefined
): string | undefined {
  if (!step || !fieldId) return undefined;

  const [prefix] = fieldId.split('.');
  const owner = (step.modules ?? []).find((formModule) => {
    if (
      formModule.repeatable?.enabled &&
      (formModule.saveKey || formModule.id) === prefix
    ) {
      return true;
    }
    return (formModule.components ?? []).some(
      (component) => component.fieldId === fieldId
    );
  });
  return owner?.id;
}
